import { useEffect, useState } from "react";
import { conversationsApi } from "../services/conversationsApi";

// Os tipos que se desenham a partir do proprio blob (<img>/<audio>), e nao um
// PDF ou qualquer outro tipo que cai no chip de download.
//
// Uma imagem precisa de pixeis e um audio precisa de alguma coisa para o
// elemento carregar. Um PDF cai no chip, cuja etiqueta inteira, nome, tipo e
// tamanho, sai do bloco que ja esta em maos, portanto pedir os bytes comprava um
// chip que nao desenhava nada de novo.
//
// NAS IMAGENS, UM ALLOWLIST RASTER E NAO "startsWith image/". image/svg+xml e
// um documento com script, e um blob: herda a origem de quem o mintou (o
// backoffice, onde vivem access_token e refresh_token). accepted_mime no
// backend e uma allowlist pura sem denylist, portanto isto so fica vivo no dia
// em que um tenant meter svg na config, mas o guarda tem de estar aqui e nao
// la. Esta mesma funcao decide, mais abaixo, com que tipo o blob e mintado: o
// contentType que aqui passou, para o que se desenha, ou octet-stream para o
// resto. Nenhum dos dois ramos fica com o Content-Type que o servidor mandou,
// que vem doutra fonte. Os dois usos tem de concordar, senao um dos lados fica
// a assumir um "desenhavel" que o outro ja mudou.
//
// O QUE ISTO CUSTA e a razao de existir. A transcricao nao e virtualizada, todos
// os baloes montam de uma vez, e o widget mediu o mesmo caso: seis PDFs de 8 MB
// puxavam cerca de 48 MB no momento em que o painel abria. Do lado do servidor
// cada GET le o objeto inteiro para memoria, perto do dobro do
// attachment_max_bytes, sem rate limit a frente.
const RASTER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

export function drawsFromItsBytes(contentType) {
  if (!contentType) return false;
  return contentType.startsWith("audio/") || RASTER_IMAGE_TYPES.has(contentType);
}

/**
 * O URL de onde desenhar este anexo, ou null enquanto ainda nao ha um.
 *
 * QUATRO CAMPOS E NAO UM, porque `url === null` cobre tres estados diferentes:
 * parado (um chip que ninguem clicou), em voo, e recusado. Quem le so o URL
 * desenha os tres igual, que e como um anexo recusado passa a parecer um PDF que
 * funciona e um em voo passa a ser um botao que nao faz nada.
 *
 * O URL E DESTE HOOK E DE MAIS NINGUEM. Um createObjectURL por anexo desenhado,
 * revogado na limpeza do efeito, portanto o URL morre com o componente que o
 * desenhou.
 *
 * AS DEPENDENCIAS SAO TODAS PRIMITIVAS, DE PROPOSITO, e e a linha mais
 * importante do ficheiro. A transcricao e substituida por objetos novos duas
 * vezes por duas vias: o poll de POLL_MS do proprio ConversationDetail, travado
 * enquanto o stream ao vivo da 6.2 estiver ligado, menos quando uma mensagem do
 * cliente espera pela ponte de handoff (o `awaitingBridge`), que o poe a correr
 * ao lado do stream; e o refetch do useConversationLive quando esta, coalescido
 * a 250 ms. (Nao e o setInterval do
 * ConversationsWorkspace: esse recarrega a lista.) As mensagens tem key={m.id} e
 * o detalhe vive sob key={selectedId}, portanto nada desmonta; mas com o objeto
 * de argumentos nas dependencias, o efeito voltava a correr a cada substituicao
 * e refazia o fetch de todas as imagens e notas de voz no ecra, para sempre, sem
 * que nada mudasse no que se ve.
 */
export function useAttachmentBlob({ conversationId, attachmentId, contentType }) {
  const [minted, setMinted] = useState(null);
  const [failed, setFailed] = useState(false);
  // UM CONTADOR E NAO UMA FLAG, a fazer dois trabalhos com um campo. Zero quer
  // dizer que ninguem pediu, que e onde um chip descansa ate ser clicado. Cada
  // incremento e um pedido novo, e como esta nas dependencias do efeito, um
  // incremento refaz o fetch: e isso o retry inteiro. Um booleano ja estava true
  // no segundo clique, nao mudava dependencia nenhuma, e deixava morto para o
  // resto da pagina um chip que perdeu um pedido a um deploy.
  const [attempt, setAttempt] = useState(0);

  const rendersInline = drawsFromItsBytes(contentType);
  const needsFetch = rendersInline || attempt > 0;

  useEffect(() => {
    if (!needsFetch) return undefined;
    if (!conversationId || !attachmentId) {
      // So um bloco malformado chega aqui: o Turn passa payload={b.payload ||
      // {}}, portanto attachmentId pode faltar so pelos dados, mesmo sem
      // nenhum escritor a esquecer-se de o por. Termina aqui de proposito,
      // para que esse bloco vire um chip falhado em vez de um spinner eterno.
      // Sem isto, `loading` (needsFetch && !minted && !failed) ficava true
      // para sempre, um spinner sem estado terminal e sem retry.
      setFailed(true);
      return undefined;
    }
    let url = null;
    let live = true;
    (async () => {
      try {
        const blob = await conversationsApi.getAttachmentBlob(conversationId, attachmentId);
        // `live` e nao so um null check: um unmount entre o await e aqui mintava
        // um URL sem ninguem para o revogar.
        if (!live) return;
        // As duas alternativas re-tipam o blob, nenhuma fica com o que o
        // axios trouxe. rendersInline usa contentType, o mesmo valor que ja
        // passou no allowlist (drawsFromItsBytes), porque o header
        // Content-Type da resposta vem doutro sitio (attachment_egress le-o
        // do proprio objeto no S3/MinIO) e nada obriga os dois a concordar.
        // Tudo o resto (PDF, um documento qualquer, ou uma imagem fora do
        // raster allowlist) sai re-tipado para application/octet-stream: o
        // chip so usa este URL num <a download>, mas um blob: tambem responde
        // a "abrir link numa nova aba" do menu de contexto, que ignora o
        // atributo download, e octet-stream e o que torna essa navegacao um
        // descarregamento em vez de um documento a render na nossa origem.
        const blobForUrl = rendersInline
          ? new Blob([blob], { type: contentType })
          : new Blob([blob], { type: "application/octet-stream" });
        url = URL.createObjectURL(blobForUrl);
        setMinted(url);
      } catch {
        // O status e nao o detalhe: com responseType blob o axios nao
        // desserializa o corpo do erro, portanto err.response.data.detail e
        // undefined mesmo num 404 que traz JSON. Aqui nao ha nada a dizer ao
        // operador para alem de "nao deu", que e o que o `failed` transporta.
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
      if (url) URL.revokeObjectURL(url);
      setMinted(null);
      setFailed(false);
    };
    // `attempt` esta aqui pelo efeito que tem na identidade e nao porque o corpo
    // o leia: incrementa-lo e o que desmonta a corrida anterior e comeca outra.
    // Tira-lo como "nao usado" transforma cada retry num no-op.
  }, [needsFetch, conversationId, attachmentId, attempt, rendersInline, contentType]);

  return {
    url: minted,
    failed,
    loading: needsFetch && !minted && !failed,
    request: () => setAttempt((n) => n + 1),
  };
}
